import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, FileCheck2, ShieldCheck, Scale } from "lucide-react";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import NestedTabs from "@/pages/SuperAdmin/Setting/NestedTabs";


interface TermsConditionsModalProps {
    open: boolean;
    onClose: () => void;
    onAccept: () => void;
    loading?: boolean;
}

const TermsConditionsModal: React.FC<TermsConditionsModalProps> = ({
    open,
    onClose,
    onAccept,
    loading = false,
}) => {
    const [agreed, setAgreed] = useState(false);


    useEffect(() => {
        if (!open) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = original;
        };
    }, [open]);

    if (!open) return null;

    const canAccept = agreed && !loading;

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <FileCheck2 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h2 className="font-display text-base font-semibold text-slate-800">
                                Policy & Terms and Conditions
                            </h2>
                            <p className="text-xs text-slate-500">
                                Please review both documents and accept to continue
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* NestedTabs */}
                <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
                    <Tabs defaultValue="policy" className="w-full">
                        <TabsList className="bg-slate-100/80 flex py-4 flex-nowrap overflow-x-auto overflow-y-hidden h-fit gap-2 rounded-lg border border-slate-200/60 shadow-inner mb-4 scroll-bar-hidden items-center">
                            <TabsTrigger
                                value="policy"
                                className="rounded-lg py-2.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white cursor-pointer"
                            >
                                <ShieldCheck className="w-4 h-4" />
                                <span>Policy</span>
                            </TabsTrigger>

                            <TabsTrigger
                                value="terms"
                                className="rounded-lg py-2.5 px-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-primary data-[state=active]:border-primary/20 border border-transparent flex-1 font-semibold transition-all flex items-center justify-center gap-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 data-[state=active]:hover:bg-white cursor-pointer"
                            >
                                <Scale className="w-4 h-4" />
                                <span>Terms & Conditions</span>
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="policy" className="mt-0 focus-visible:outline-none">
                            <NestedTabs category="policy" />
                        </TabsContent>

                        <TabsContent value="terms" className="mt-0 focus-visible:outline-none">
                            <NestedTabs category="terms" />
                        </TabsContent>
                    </Tabs>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-100 space-y-3 shrink-0">
                    <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            className="mt-0.5 w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                        />
                        <span>
                            I have read and agree to the Policy and Terms & Conditions
                            documents above
                        </span>
                    </label>

                    <div className="flex items-center justify-end gap-3 pt-1">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors cursor-pointer"
                        >
                            Later
                        </button>
                        <button
                            onClick={onAccept}
                            disabled={!canAccept}
                            className="px-5 py-2 text-sm font-semibold rounded-xl bg-primary text-white shadow-lg shadow-primary/30 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition-all cursor-pointer"
                        >
                            {loading ? "Accepting..." : "Accept & Continue"}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TermsConditionsModal;